import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import Icon from '../../components/AppIcon';
import { useLanguage } from '../../contexts/LanguageContext';

const Login = () => {
  const navigate = useNavigate();
  const { language, toggleLanguage, t } = useLanguage();
  const [mode, setMode] = useState('login');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [resetEmail, setResetEmail] = useState('');
  const [resetSuccess, setResetSuccess] = useState(false);

  const [loginForm, setLoginForm] = useState({ email: '', password: '' });
  const [registerForm, setRegisterForm] = useState({
    full_name: '',
    email: '',
    password: '',
    confirm_password: '',
    role: 'guest',
  });

  // Real-time stats state
  const [stats, setStats] = useState({
    totalRooms: 0,
    totalBuildings: 0,
    occupancyRate: 0,
    totalResidents: 0,
  });

  const fetchStats = useCallback(async () => {
    try {
      // Total active rooms
      const { count: roomCount } = await supabase
        .from('rooms')
        .select('*', { count: 'exact', head: true })
        .eq('is_active', true);

      // Total distinct active buildings
      const { data: buildingData } = await supabase
        .from('rooms')
        .select('building')
        .eq('is_active', true);

      const uniqueBuildings = buildingData
        ? new Set(buildingData.map((r) => r?.building).filter(Boolean)).size
        : 0;

      // Total residents currently checked in (stays with guest_status = 'checked-in')
      const { count: checkedInCount } = await supabase
        .from('stays')
        .select('*', { count: 'exact', head: true })
        .eq('guest_status', 'checked-in');

      const totalRooms = roomCount || 0;
      const totalBuildings = uniqueBuildings || 0;
      const totalResidents = checkedInCount || 0;
      const occupancyRate =
        totalRooms > 0 ? Math.round((totalResidents / totalRooms) * 100) : 0;

      setStats({ totalRooms, totalBuildings, occupancyRate, totalResidents });
    } catch {
      // On error keep current stats (or zeros on first load)
    }
  }, []);

  useEffect(() => {
    fetchStats();

    // Periodic refresh every 60 seconds
    const interval = setInterval(fetchStats, 60000);

    // Supabase realtime subscriptions for live updates
    const roomsChannel = supabase
      .channel('login-stats-rooms')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'rooms' }, fetchStats)
      .subscribe();

    const staysChannel = supabase
      .channel('login-stats-stays')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'stays' }, fetchStats)
      .subscribe();

    const reservationsChannel = supabase
      .channel('login-stats-reservations')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'reservation_requests' }, fetchStats)
      .subscribe();

    return () => {
      clearInterval(interval);
      supabase.removeChannel(roomsChannel);
      supabase.removeChannel(staysChannel);
      supabase.removeChannel(reservationsChannel);
    };
  }, [fetchStats]);

  const handleLogin = async (e) => {
    e?.preventDefault();
    setError('');
    setLoading(true);
    try {
      const { data, error: signInError } = await supabase?.auth?.signInWithPassword({
        email: loginForm?.email,
        password: loginForm?.password,
      });
      if (signInError) {
        setError(signInError?.message);
      } else {
        const userRole = data?.user?.user_metadata?.role || 'guest';
        if (userRole === 'guest') {
          navigate('/reservation-request');
        } else {
          navigate('/occupancy-analytics');
        }
      }
    } catch {
      setError(t?.login?.networkError);
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e?.preventDefault();
    setError('');
    if (registerForm?.password !== registerForm?.confirm_password) { setError(t?.login?.passwordMismatch); return; }
    if (registerForm?.password?.length < 6) { setError(t?.login?.passwordTooShort); return; }
    setLoading(true);
    try {
      const { error: signUpError } = await supabase?.auth?.signUp({
        email: registerForm?.email,
        password: registerForm?.password,
        options: { data: { full_name: registerForm?.full_name, role: 'guest' } },
      });
      if (signUpError) {
        setError(signUpError?.message);
      } else {
        navigate('/reservation-request');
      }
    } catch {
      setError(t?.login?.networkError);
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e?.preventDefault();
    setError('');
    setLoading(true);
    try {
      const { error: resetError } = await supabase?.auth?.resetPasswordForEmail(resetEmail, {
        redirectTo: `https://sengo.usenghor.org/update-password`,
      });
      if (resetError) {
        setError(resetError?.message);
      } else {
        setResetSuccess(true);
      }
    } catch {
      setError(t?.login?.networkError);
    } finally {
      setLoading(false);
    }
  };

  const inputClass = "premium-input";

  const statCards = [
    { labelKey: 'totalRooms', value: stats.totalRooms.toLocaleString(), icon: 'DoorOpen' },
    { labelKey: 'buildings', value: stats.totalBuildings.toLocaleString(), icon: 'Building2' },
    { labelKey: 'occupancy', value: `${stats.occupancyRate}%`, icon: 'TrendingUp' },
    { labelKey: 'residents', value: stats.totalResidents.toLocaleString(), icon: 'Users' },
  ];

  return (
    <div className="min-h-screen bg-background flex">
      {/* Left decorative panel — hidden on mobile */}
      <div className="hidden lg:flex lg:w-[45%] xl:w-[50%] relative overflow-hidden flex-col justify-between p-12"
        style={{ background: 'linear-gradient(160deg, #1B2D5B 0%, #0F1A35 50%, #0A1020 100%)' }}>
        {/* Decorative circles */}
        <div className="absolute top-0 right-0 w-96 h-96 rounded-full opacity-10"
          style={{ background: 'radial-gradient(circle, #B8963E 0%, transparent 70%)', transform: 'translate(30%, -30%)' }} />
        <div className="absolute bottom-0 left-0 w-80 h-80 rounded-full opacity-8"
          style={{ background: 'radial-gradient(circle, #4A7FD4 0%, transparent 70%)', transform: 'translate(-30%, 30%)' }} />

        <div className="relative z-10">
          <div className="flex items-center gap-3 mb-16">
            <img
              src="/assets/images/logo_USenghor_vertical_blanc_1000_min-1774525392061.png"
              alt="Université Senghor logo"
              className="w-12 h-12 object-contain"
            />
            <div>
              <div className="text-white font-bold text-sm" style={{ fontFamily: 'Manrope, sans-serif' }}>US Réservation</div>
              <div className="text-white/40 text-xs">Résidences</div>
            </div>
          </div>

          <h2 className="text-4xl font-bold text-white leading-tight mb-4" style={{ fontFamily: 'Manrope, sans-serif', letterSpacing: '-0.03em' }}>
            {t?.login?.premiumResidence}<br />{t?.login?.management}
          </h2>
          <p className="text-white/50 text-base leading-relaxed max-w-sm">
            {t?.login?.streamlineOps}
          </p>
        </div>

        <div className="relative z-10 grid grid-cols-2 gap-4">
          {statCards?.map((stat) => (
            <div key={stat?.labelKey} className="rounded-xl p-4"
              style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.08)' }}>
              <Icon name={stat?.icon} size={16} color="rgba(184,150,62,0.8)" />
              <div className="text-2xl font-bold text-white mt-2" style={{ fontFamily: 'Manrope, sans-serif' }}>{stat?.value}</div>
              <div className="text-white/40 text-xs mt-0.5">{t?.login?.[stat?.labelKey]}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Right — form panel */}
      <div className="flex-1 flex items-center justify-center p-6 sm:p-10">
        <div className="w-full max-w-[400px]">
          {/* Mobile logo */}
          <div className="lg:hidden flex items-center gap-3 mb-8">
            <img
              src="/assets/images/logo_USenghor_vertical_blanc_1000_min-1774525392061.png"
              alt="Université Senghor logo"
              className="w-10 h-10 object-contain"
            />
            <div>
              <div className="font-bold text-foreground text-sm" style={{ fontFamily: 'Manrope, sans-serif' }}>US Réservation Résidences</div>
              <div className="text-muted-foreground text-xs">{language === 'fr' ? 'Système de gestion des résidents' : 'Resident Management System'}</div>
            </div>
          </div>

          {/* Language toggle on login page */}
          <div className="flex justify-end mb-4">
            <button
              onClick={toggleLanguage}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold border border-border text-muted-foreground hover:text-foreground hover:border-foreground/30 transition-all"
            >
              <Icon name="Globe" size={13} />
              {language === 'en' ? 'Français' : 'English'}
            </button>
          </div>

          <div className="mb-7">
            <h1 className="text-2xl font-bold text-foreground" style={{ fontFamily: 'Manrope, sans-serif', letterSpacing: '-0.02em' }}>
              {mode === 'login' ? t?.login?.welcomeBack : t?.login?.createAccount}
            </h1>
            <p className="text-muted-foreground text-sm mt-1">
              {mode === 'login' ? t?.login?.signInToContinue : t?.login?.registerToAccess}
            </p>
          </div>

          {/* Tabs */}
          <div className="flex gap-1 p-1 bg-muted rounded-lg mb-6">
            {['login', 'register']?.map((tab) => (
              <button
                key={tab}
                onClick={() => { setMode(tab); setError(''); }}
                className={`flex-1 py-2 text-sm font-semibold rounded-md transition-all duration-200 ${
                  mode === tab
                    ? 'bg-card text-foreground shadow-sm'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                {tab === 'login' ? t?.login?.signIn : t?.login?.register}
              </button>
            ))}
          </div>

          {/* Error */}
          {error && (
            <div className="mb-5 p-3 rounded-lg flex items-start gap-2.5"
              style={{ background: 'rgba(192,57,43,0.08)', border: '1px solid rgba(192,57,43,0.2)' }}>
              <Icon name="AlertCircle" size={15} className="text-error mt-0.5 flex-shrink-0" />
              <p className="text-error text-sm">{error}</p>
            </div>
          )}

          {mode === 'login' ? (
            <form onSubmit={handleLogin} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-1.5">{t?.login?.emailAddress}</label>
                <input type="email" required value={loginForm?.email}
                  onChange={(e) => setLoginForm({ ...loginForm, email: e?.target?.value })}
                  placeholder="you@example.com" className={inputClass} />
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-1.5">{t?.login?.password}</label>
                <input type="password" required value={loginForm?.password}
                  onChange={(e) => setLoginForm({ ...loginForm, password: e?.target?.value })}
                  placeholder="••••••••" className={inputClass} />
              </div>
              <button type="submit" disabled={loading}
                className="w-full py-2.5 rounded-lg text-sm font-semibold transition-all duration-200 disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2 mt-2"
                style={{ background: 'var(--color-primary)', color: '#fff' }}>
                {loading ? <><Icon name="Loader2" size={16} className="animate-spin" /> {t?.login?.signingIn}</> : t?.login?.signIn}
              </button>
              <div className="text-center mt-3">
                <button
                  type="button"
                  onClick={() => { setMode('forgot'); setError(''); setResetSuccess(false); }}
                  className="text-sm text-muted-foreground hover:text-foreground transition-colors underline-offset-2 hover:underline"
                >
                  {t?.login?.forgotPassword}
                </button>
              </div>
            </form>
          ) : mode === 'forgot' ? (
            <div>
              {resetSuccess ? (
                <div className="text-center py-4">
                  <div className="w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4"
                    style={{ background: 'rgba(34,197,94,0.1)', border: '1px solid rgba(34,197,94,0.2)' }}>
                    <Icon name="MailCheck" size={22} className="text-green-500" />
                  </div>
                  <p className="text-sm text-foreground font-medium mb-1">{t?.login?.resetEmailSent}</p>
                  <button
                    onClick={() => { setMode('login'); setResetSuccess(false); setError(''); }}
                    className="mt-4 text-sm text-muted-foreground hover:text-foreground transition-colors underline-offset-2 hover:underline flex items-center gap-1.5 mx-auto"
                  >
                    <Icon name="ArrowLeft" size={14} />
                    {t?.login?.backToSignIn}
                  </button>
                </div>
              ) : (
                <form onSubmit={handleForgotPassword} className="space-y-4">
                  <p className="text-sm text-muted-foreground mb-2">{t?.login?.resetPasswordDesc}</p>
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-1.5">{t?.login?.emailAddress}</label>
                    <input type="email" required value={resetEmail}
                      onChange={(e) => setResetEmail(e?.target?.value)}
                      placeholder="you@example.com" className={inputClass} />
                  </div>
                  <button type="submit" disabled={loading}
                    className="w-full py-2.5 rounded-lg text-sm font-semibold transition-all duration-200 disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2 mt-2"
                    style={{ background: 'var(--color-primary)', color: '#fff' }}>
                    {loading ? <><Icon name="Loader2" size={16} className="animate-spin" /> {t?.login?.sendingResetLink}</> : t?.login?.sendResetLink}
                  </button>
                  <div className="text-center mt-1">
                    <button
                      type="button"
                      onClick={() => { setMode('login'); setError(''); }}
                      className="text-sm text-muted-foreground hover:text-foreground transition-colors underline-offset-2 hover:underline flex items-center gap-1.5 mx-auto"
                    >
                      <Icon name="ArrowLeft" size={14} />
                      {t?.login?.backToSignIn}
                    </button>
                  </div>
                </form>
              )}
            </div>
          ) : (
            <form onSubmit={handleRegister} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-1.5">{t?.login?.fullName}</label>
                <input type="text" required value={registerForm?.full_name}
                  onChange={(e) => setRegisterForm({ ...registerForm, full_name: e?.target?.value })}
                  placeholder={t?.login?.yourFullName} className={inputClass} />
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-1.5">{t?.login?.emailAddress}</label>
                <input type="email" required value={registerForm?.email}
                  onChange={(e) => setRegisterForm({ ...registerForm, email: e?.target?.value })}
                  placeholder="you@example.com" className={inputClass} />
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-1.5">{t?.login?.password}</label>
                <input type="password" required value={registerForm?.password}
                  onChange={(e) => setRegisterForm({ ...registerForm, password: e?.target?.value })}
                  placeholder={t?.login?.minPassword} className={inputClass} />
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-1.5">{t?.login?.confirmPassword}</label>
                <input type="password" required value={registerForm?.confirm_password}
                  onChange={(e) => setRegisterForm({ ...registerForm, confirm_password: e?.target?.value })}
                  placeholder={t?.login?.repeatPassword} className={inputClass} />
              </div>
              <button type="submit" disabled={loading}
                className="w-full py-2.5 rounded-lg text-sm font-semibold transition-all duration-200 disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2 mt-2"
                style={{ background: 'var(--color-primary)', color: '#fff' }}>
                {loading ? <><Icon name="Loader2" size={16} className="animate-spin" /> {t?.login?.creatingAccount}</> : t?.login?.register}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default Login;
